/**
 * Os status que encerram um chamado.
 *
 * ## Por que esta lista existe aqui, de novo
 *
 * Ela é a cópia da `TicketStatus::NOMES_FINALIZADOS` da api. Os dois lados
 * precisam dela porque a tela também decide coisas sozinha: quais chamados
 * finalizados buscar sob demanda, e como avaliar um filtro sobre as linhas
 * já carregadas.
 *
 * Antes deste arquivo, o array literal `['Fechado', 'Cancelado', 'Resolvido']`
 * aparecia escrito à mão dentro da `TicketPage`. O risco de duplicata não é
 * teórico: um status novo na api sem o mesmo nome aqui faz a tela esconder
 * chamados **sem nenhum erro**, que é o modo de falha mais caro que existe
 * nesta tela.
 *
 * ## O que é "concluído", e o chamado SEM status
 *
 * Concluído é estar nesta lista. Qualquer outra coisa — inclusive **não ter
 * status** — é não concluído. Isso não é tecnicismo: chamado com `status_id`
 * nulo existe em produção, e foi uma das três causas da divergência entre os
 * cards de indicadores e a listagem, consertada em 02/10. Um chamado sem
 * status não está resolvido nem fechado nem cancelado, logo está em aberto.
 */
export const NOMES_FINALIZADOS = ['Fechado', 'Cancelado', 'Resolvido'];

/**
 * Os ids dos status finais, resolvidos contra os status cadastrados.
 *
 * @param {Array<{id: number|string, name: string}>} statuses
 * @returns {number[]}
 */
export const idsFinalizados = (statuses) => (Array.isArray(statuses) ? statuses : [])
    .filter((st) => NOMES_FINALIZADOS.includes(st?.name))
    .map((st) => Number(st.id))
    .filter((id) => !Number.isNaN(id));

/** O `status_id` do chamado, nas três formas em que a api já o entregou. */
export const statusIdDoChamado = (ticket) => ticket?.status_id
    ?? ticket?.ticket_status?.id
    ?? ticket?.ticketStatus?.id
    ?? null;

/** O nome do status do chamado, nas formas em que a api o entrega. */
export const nomeDoStatusDoChamado = (ticket) => ticket?.ticketStatus?.name
    ?? ticket?.ticket_status?.name
    ?? null;

/**
 * O chamado está concluído?
 *
 * ## Por que duas fontes, e por que isso NÃO é duas definições
 *
 * A regra é uma só: estar em `NOMES_FINALIZADOS`. O que varia é de onde o
 * dado vem na linha que a tela tem em mãos.
 *
 * - **Por id**, quando recebe `finalizados`. É o caminho exato, e o mesmo que
 *   o servidor usa (ele resolve os nomes para ids uma vez por empresa). Quem
 *   tem a lista de status cadastrados passa ela e ganha isto.
 * - **Por nome**, como reserva. Nem toda tela carrega os status — a
 *   `ClientTicketsPage` não — e a api manda `ticketStatus:id,name`, então o
 *   nome costuma estar ali.
 *
 * Os dois respondem igual quando os dois dados existem. A reserva existe
 * porque a alternativa seria a tela sem a lista não filtrar nada.
 *
 * Sem status nenhum => **não concluído**: um chamado sem status não está
 * resolvido, nem fechado, nem cancelado.
 *
 * @param {object} ticket
 * @param {number[]} [finalizados]  ids finais, de `idsFinalizados`
 */
export const ehConcluido = (ticket, finalizados) => {
    const sid = statusIdDoChamado(ticket);
    const lista = Array.isArray(finalizados) ? finalizados : [];

    if (sid != null && lista.length > 0) {
        return lista.some((id) => Number(id) === Number(sid));
    }

    const nome = nomeDoStatusDoChamado(ticket);

    return nome != null && NOMES_FINALIZADOS.includes(nome);
};
